/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import inventoryApi from '../../../services/inventoryApi';
import InventoryLayout, { 
  Card, 
  CardHeader, 
  ActionButton, 
  StatusBadge, 
  ErrorState 
} from '../../../components/admin/InventoryLayout';

const InventorySettings = () => {
  const { user, hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications: {
      immediateAlerts: true,
      dailyDigest: true,
      digestTime: '09:00',
      recipients: {
        shop_admin: true,
        barber: false,
        client: false
      }
    },
    alerts: {
      lowStockThreshold: 10,
      criticalStockThreshold: 5,
      expiryWarningDays: 30
    },
    general: {
      autoArchiveOldItems: false,
      defaultUnit: 'piece',
      requireApprovalForAdjustments: true,
      trackExpiryDates: true
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load settings from API - for now using defaults
      // const response = await inventoryApi.settings.getSettings();
      // setSettings(response.settings || defaultSettings);
      
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      // await inventoryApi.settings.updateSettings(settings);
      console.log('Settings saved:', settings);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testAlert = async () => {
    try {
      setError('');
      await inventoryApi.alerts.testAlerts();
      // Show success message
      console.log('Test alert sent successfully');
    } catch (err) {
      console.error('Error sending test alert:', err);
      setError('Failed to send test alert');
    }
  };

  const handleNotificationChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value
      }
    }));
  };

  const handleRecipientChange = (role, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        recipients: {
          ...prev.notifications.recipients,
          [role]: value
        }
      }
    }));
  };

  const handleAlertChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      alerts: {
        ...prev.alerts,
        [key]: value
      }
    }));
  };

  const handleGeneralChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        [key]: value
      }
    }));
  };

  return (
    <InventoryLayout 
      title="Inventory Settings"
      subtitle="Configure inventory management preferences and notifications"
      currentTab="settings"
    >
      {/* Mobile Header - Like Bookings Page */}
      <div className="md:hidden mb-6 pt-2">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#7d6633]/10 flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Settings</h1>
            <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
              Configure inventory management preferences and notifications
            </p>
          </div>
        </div>
      </div>

      {error && <ErrorState error={error} onRetry={loadSettings} />}

      {/* Notification Settings */}
      <Card>
        <CardHeader
          title="Notification Settings"
          subtitle="Configure inventory alerts and notifications"
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM15 17H9a4 4 0 01-4-4v-4a4 4 0 014-4h6a4 4 0 014 4v4a4 4 0 01-4 4z" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5 space-y-6">
          {/* Immediate Alerts */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Immediate Alerts</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Send alerts immediately when stock goes below threshold</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={settings.notifications.immediateAlerts}
                onChange={(e) => handleNotificationChange('immediateAlerts', e.target.checked)}
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#7d6633]/20 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-[#7d6633]"></div>
                </label>
              </div>

              {/* Daily Digest */}
              <div className="flex items-center justify-between">
                <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Daily Digest</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Send daily summary of low stock items</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={settings.notifications.dailyDigest}
                onChange={(e) => handleNotificationChange('dailyDigest', e.target.checked)}
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#7d6633]/20 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-[#7d6633]"></div>
                </label>
              </div>

              {/* Digest Time */}
          {settings.notifications.dailyDigest && (
              <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Daily Digest Time</label>
              <select 
                value={settings.notifications.digestTime}
                onChange={(e) => handleNotificationChange('digestTime', e.target.value)}
                className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              >
                <option value="08:00">08:00 AM</option>
                  <option value="09:00">09:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                </select>
              </div>
          )}

          {/* Notification Recipients */}
              <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-4">Notification Recipients</label>
            <div className="space-y-3">
                  <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={settings.notifications.recipients.shop_admin}
                  onChange={(e) => handleRecipientChange('shop_admin', e.target.checked)}
                  className="h-4 w-4 text-[#7d6633] focus:ring-[#7d6633]/50 border-zinc-300 dark:border-zinc-600 rounded bg-white/50 dark:bg-zinc-800/50"
                />
                <span className="ml-2 text-sm text-zinc-700 dark:text-zinc-300">Shop Admins</span>
                  </label>
                  <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={settings.notifications.recipients.barber}
                  onChange={(e) => handleRecipientChange('barber', e.target.checked)}
                  className="h-4 w-4 text-[#7d6633] focus:ring-[#7d6633]/50 border-zinc-300 dark:border-zinc-600 rounded bg-white/50 dark:bg-zinc-800/50"
                />
                <span className="ml-2 text-sm text-zinc-700 dark:text-zinc-300">Barbers</span>
                  </label>
                  <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={settings.notifications.recipients.client}
                  onChange={(e) => handleRecipientChange('client', e.target.checked)}
                  className="h-4 w-4 text-[#7d6633] focus:ring-[#7d6633]/50 border-zinc-300 dark:border-zinc-600 rounded bg-white/50 dark:bg-zinc-800/50"
                />
                <span className="ml-2 text-sm text-zinc-700 dark:text-zinc-300">Clients</span>
                  </label>
                </div>
              </div>

          {/* Test Alert Button */}
              <div className="pt-4">
            <ActionButton
              onClick={testAlert}
              variant="accent"
              size="md"
              className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
              </svg>
              Send Test Alert
            </ActionButton>
          </div>
        </div>
      </Card>

      {/* Alert Thresholds */}
      <Card>
        <CardHeader
          title="Alert Thresholds"
          subtitle="Configure when to trigger stock alerts"
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Low Stock Threshold (Default)
            </label>
            <input
              type="number"
              min="1"
              value={settings.alerts.lowStockThreshold}
              onChange={(e) => handleAlertChange('lowStockThreshold', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="10"
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Alert when stock falls below this quantity
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Critical Stock Threshold (Default)
            </label>
            <input
              type="number"
              min="1"
              value={settings.alerts.criticalStockThreshold}
              onChange={(e) => handleAlertChange('criticalStockThreshold', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="5"
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Critical alert when stock falls below this quantity
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Expiry Warning Days
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={settings.alerts.expiryWarningDays}
              onChange={(e) => handleAlertChange('expiryWarningDays', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
              placeholder="30"
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Alert when items expire within this many days
            </p>
          </div>
        </div>
      </Card>

      {/* General Settings */}
      <Card>
        <CardHeader
          title="General Settings"
          subtitle="Configure general inventory management behavior"
          icon={
            <svg className="h-5 w-5 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5 space-y-6">
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Default Unit of Measurement
            </label>
            <select 
              value={settings.general.defaultUnit}
              onChange={(e) => handleGeneralChange('defaultUnit', e.target.value)}
              className="w-full px-3 py-2 border border-white/20 dark:border-white/10 rounded-lg bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#7d6633]/50"
            >
              <option value="piece">Piece</option>
              <option value="bottle">Bottle</option>
              <option value="tube">Tube</option>
              <option value="jar">Jar</option>
              <option value="pack">Pack</option>
              <option value="liter">Liter</option>
              <option value="ml">Milliliter</option>
              <option value="gram">Gram</option>
              <option value="kg">Kilogram</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Track Expiry Dates</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Enable expiry date tracking for inventory items</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={settings.general.trackExpiryDates}
                onChange={(e) => handleGeneralChange('trackExpiryDates', e.target.checked)}
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#7d6633]/20 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-[#7d6633]"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Require Approval for Adjustments</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Adjustments above certain amounts require admin approval</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={settings.general.requireApprovalForAdjustments}
                onChange={(e) => handleGeneralChange('requireApprovalForAdjustments', e.target.checked)}
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#7d6633]/20 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-[#7d6633]"></div>
            </label>
              </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Auto-Archive Old Items</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Automatically archive items with no activity for 12+ months</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={settings.general.autoArchiveOldItems}
                onChange={(e) => handleGeneralChange('autoArchiveOldItems', e.target.checked)}
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#7d6633]/20 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-[#7d6633]"></div>
            </label>
          </div>
        </div>
      </Card>

      {/* Save Settings Button */}
      <div className="flex justify-end">
        <ActionButton
          onClick={saveSettings}
          variant="accent"
          size="lg"
          disabled={saving}
          className="!bg-[#7d6633] !text-white hover:!bg-[#6a5429]"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {saving ? 'Saving Settings...' : 'Save Settings'}
        </ActionButton>
      </div>
    </InventoryLayout>
  );
};

export default InventorySettings;